import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@core/auth/authentication.service';
import { ErrorService } from '../../../../../shared/services/error.service';
import { ClientesService } from './clientes.service';
import { Cliente, ClienteFiltros, EstadoContrato } from './clientes.interface';
import { BadgeColor } from '../../../../../shared/components/ui/badge/badge';
import { PageHeaderComponent } from '../../../../../shared/components/common/page-header/page-header.component';
import { Badge } from '../../../../../shared/components/ui/badge/badge';
import { DatePipe } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import {
  Pagination,
  SearchInput,
  FilterSelect,
  FilterOption,
  DataTable,
  TableColumn,
  TableCellDirective,
  FiltersContainer
} from '../../../../../shared/components/ui/list-components';

@Component({
  selector: 'app-clientes',
  imports: [
    PageHeaderComponent,
    Badge,
    DatePipe,
    MatTooltip,
    Pagination,
    SearchInput,
    FilterSelect,
    DataTable,
    TableCellDirective,
    FiltersContainer
  ],
  templateUrl: './clientes.html'
})
export class Clientes implements OnInit {
  readonly permissionAuth = inject(AuthenticationService);

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private errorService = inject(ErrorService);
  private clientesService = inject(ClientesService);

  clientes: Cliente[] = [];
  loading = false;

  // Paginación
  currentPage = 1;
  lastPage = 1;
  perPage = 15;
  total = 0;

  // Filtros
  filtros: ClienteFiltros = {
    busqueda: '',
    tipo: '',
    estado_contrato: ''
  };

  // Columnas de la tabla
  columns: TableColumn[] = [
    { key: 'persona', header: 'Persona' },
    { key: 'documento', header: 'Documento' },
    { key: 'tipo_persona', header: 'Tipo persona' },
    { key: 'tipo_cliente', header: 'Tipo cliente' },
    { key: 'razon_social', header: 'Razón social' },
    { key: 'contrato', header: 'Contrato' },
    { key: 'fecha_conversion', header: 'Fecha de conversión' }
  ];

  // Opciones de filtros
  tipoOptions: FilterOption[] = [
    { value: 'F', label: 'Persona física' },
    { value: 'J', label: 'Persona jurídica' }
  ];

  // Los cuatro estados son excluyentes y se resuelven por precedencia en el backend:
  // activo > vencido > cerrado > sin_contrato (ver Cliente::ESTADOS_CONTRATO).
  estadoContratoOptions: FilterOption[] = [
    { value: 'activo', label: 'Contrato activo' },
    { value: 'vencido', label: 'Contrato vencido' },
    { value: 'cerrado', label: 'Contrato cerrado' },
    { value: 'sin_contrato', label: 'Sin contrato' }
  ];

  private readonly estadoContratoBadge: Record<EstadoContrato, { label: string; color: BadgeColor; icon: string }> = {
    activo: { label: 'Activo', color: 'success', icon: 'check_circle' },
    vencido: { label: 'Vencido', color: 'error', icon: 'event_busy' },
    // Cerrar un contrato es una decisión, no un descuido: va en gris y no en rojo.
    cerrado: { label: 'Cerrado', color: 'light', icon: 'cancel' },
    sin_contrato: { label: 'Sin contrato', color: 'light', icon: 'remove_circle_outline' }
  };

  ngOnInit(): void {
    this.getClientes();
  }

  getClientes(page: number = 1): void {
    this.loading = true;
    this.clientesService.index(page, this.perPage, this.filtros).subscribe({
      next: (response) => {
        if (response.status) {
          this.clientes = response.data.data;
          this.currentPage = response.data.pagination.current_page;
          this.lastPage = response.data.pagination.last_page;
          this.total = response.data.pagination.total;
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  onSearchChange(value: string): void {
    this.filtros.busqueda = value;
    this.currentPage = 1;
    this.getClientes();
  }

  onTipoChange(value: string | number): void {
    this.filtros.tipo = value as string;
    this.currentPage = 1;
    this.getClientes();
  }

  // La celda recibe el cliente como any (contexto de appTableCell); acá se recupera el tipo
  badgeContrato(estado: EstadoContrato) {
    return this.estadoContratoBadge[estado];
  }

  onEstadoContratoChange(value: string | number): void {
    this.filtros.estado_contrato = value as EstadoContrato | '';
    this.currentPage = 1;
    this.getClientes();
  }

  get hasActiveFilters(): boolean {
    return Object.values(this.filtros).some(valor => valor !== null && valor !== undefined && valor !== '');
  }

  clearFilters(): void {
    this.filtros = {
      busqueda: '',
      tipo: '',
      estado_contrato: ''
    };
    this.currentPage = 1;
    this.getClientes();
  }

  onPageChange(page: number): void {
    this.getClientes(page);
  }

  onPerPageChange(perPage: number): void {
    this.perPage = perPage;
    this.currentPage = 1;
    this.getClientes(1);
  }

  onRowClick(event: { item: Cliente; index: number }): void {
    this.view(event.item.id);
  }

  create(): void {
    this.router.navigate(['new'], { relativeTo: this.route });
  }

  view(id: number): void {
    this.router.navigate(['./', id], { relativeTo: this.route });
  }
}
